import { useSelector } from 'react-redux';
import DateUtils from 'js/lib/DateUtils';
import { DataSource } from 'new/entities/DataSource';
import { MetricsUtil } from 'new/utils/MetricsUtil';
import {
  getConnectionOrVendorType,
  getResourceApiKeys,
  isSqlAutomation,
} from 'js/lib/ResourceUtils';
import has from 'lodash/has';
import { CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT } from 'js/constants/connectionCategories';

const NoResourceResponse = {
  setsCount: 0,
  createdDate: 0,
  lastUpdate: 0,
  metricsByDate: null,
  resourceTypeFields: null,
  connectionCategory: null,
  sourceConfig: null,
  resourceApiKeys: null,
  credentials: null,
  metrics: MetricsUtil.handleLifetimeMetrics(null),
};

export const useSourceOverview = (resource: DataSource) => {
  const state = useSelector((state: any) => state);
  const {
    paging: { sourceConfigById, metaInfoById },
    entities: { vendors, credentials: credEntities, apiKeys },
    sources: { summaryById },
  } = state;

  if (!resource || typeof resource !== 'object') {
    /**
     * if the resource was not provided for some reason
     * returning a mock object to avoid UI crashing
     */
    return NoResourceResponse;
  }

  const summaryData = resource.id in summaryById ? summaryById[resource.id] : null;
  const setsCount = summaryData?.data_sets ?? null;

  const createdDate = DateUtils.convertDateStringToFormat(resource.created_at);
  const lastUpdate = DateUtils.convertDateStringToFormat(resource.updated_at);

  const connectionOrVendorType = getConnectionOrVendorType(
    resource,
    vendors,
    'source_type',
    'vendor_id',
  );
  const behaviorType = isSqlAutomation(resource, vendors ?? {})
    ? 'sql_runner'
    : connectionOrVendorType;

  const resourceTypeFields = has(sourceConfigById, behaviorType)
    ? sourceConfigById[behaviorType]
    : [];

  const connectionCategory =
    metaInfoById && has(metaInfoById, behaviorType)
      ? metaInfoById[behaviorType].connectionCategory
      : null;

  const sourceConfig =
    connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT
      ? resource.script_config || {}
      : resource.source_config || {};

  const resourceApiKeys = getResourceApiKeys(resource, apiKeys) as any;
  const credentials = credEntities[resource?.data_credentials];
  return {
    setsCount,
    createdDate,
    lastUpdate,
    resourceTypeFields,
    connectionCategory,
    sourceConfig,
    resourceApiKeys,
    credentials,
  };
};
